import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listCustodianVaultAssets } from '../../api/custodianApi';
import { DataTable } from '../ui/DataTable';
import { ErrorBanner } from '../ui/ErrorBanner';
import { StatusPill } from '../ui/StatusPill';
import {
  custodianPackageDetailPath,
  displayValue,
} from '../../pages/custodian/custodianReviewLabels';

export function AdmittedSessionPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function loadVaultAssets() {
      setLoading(true);
      setError('');
      try {
        const response = await listCustodianVaultAssets({ page: 1, pageSize: 50 });
        if (cancelled) return;
        setRows(response?.items || []);
      } catch (requestError) {
        if (cancelled) return;
        setRows([]);
        setError(requestError.message || 'Unable to load admitted vault assets.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadVaultAssets();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const columns = [
    {
      key: 'package_id',
      header: 'Package',
      render: (row) => (
        <Link
          to={custodianPackageDetailPath(row.package_id)}
          className="font-mono text-[11px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
        >
          {displayValue(row.reference_number || row.package_id)}
        </Link>
      ),
    },
    {
      key: 'borrower',
      header: 'Borrower',
      render: (row) => displayValue(row.borrower),
    },
    {
      key: 'program',
      header: 'Program',
      render: (row) => displayValue(row.program),
    },
    {
      key: 'admission',
      header: 'Admission',
      render: (row) =>
        row.admission_state ? (
          <StatusPill value={row.admission_state} />
        ) : (
          displayValue(row.admission_state)
        ),
    },
    {
      key: 'custody',
      header: 'Custody',
      render: (row) =>
        row.custody_state ? (
          <StatusPill value={row.custody_state} />
        ) : (
          displayValue(row.custody_state)
        ),
    },
    {
      key: 'holder',
      header: 'Holder',
      render: (row) =>
        row.current_holder ? (
          <StatusPill value={row.current_holder} />
        ) : (
          displayValue(row.current_holder)
        ),
    },
  ];

  return (
    <section className="space-y-3">
      <div className="rounded-2xl border border-eorig-border bg-white p-4 shadow-eorig-card sm:p-5">
        <h2 className="text-sm font-semibold text-eorig-ink">Admitted vault assets</h2>
        <p className="mt-1 text-xs leading-relaxed text-eorig-muted">
          Packages this custodian has admitted to the vault. A package appears
          here after Admit, including after a new browser session.
        </p>
      </div>
      {error ? <ErrorBanner message={error} onRetry={() => setReloadToken((value) => value + 1)} /> : null}
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.vault_asset_id || row.package_id}
        loading={loading}
        emptyMessage="No packages have been admitted to the vault."
        minWidth={880}
      />
    </section>
  );
}
